import { hoursToMilliseconds, millisecondsToHours, millisecondsToMinutes } from '@gonfalon/datetime/dateFns';
import { daysToMilliseconds } from '@gonfalon/datetime/daysToMilliseconds';
import { millisecondsToDays } from '@gonfalon/datetime/millisecondsToDays';
import { convertFilters } from '@gonfalon/metrics/convertFilters';
import { fromMetricEventUrl } from '@gonfalon/metrics/fromMetricEventUrl';
import { isMetricEventUrl } from '@gonfalon/metrics/isMetricEventUrl';
import { MetricKind, MetricKindDisplays } from '@gonfalon/metrics/MetricKind';
import type {
  ContextAttributeFilter,
  EventPropertyFilter,
  MeasureType,
  Metric,
  MetricDataSource,
  MetricEventUrlKind,
  MetricFilter,
} from '@gonfalon/metrics/types';
import type { ResourceActions } from '@gonfalon/permissions/actions';

export type AggregationOptionKey = 'count' | 'count-distinct-units' | 'sum' | 'average';

export type AggregationOption = {
  key: AggregationOptionKey;
  name: string;
  description: string;
  tag: string;
  sentencePrefix: 'Count' | 'Percent' | 'Sum' | 'Average';
};

export const aggregationOptions: AggregationOption[] = [
  {
    key: 'count',
    name: 'Count',
    description: 'Number of times an event occurred ex: count of purchases made',
    tag: MetricKindDisplays[MetricKind.COUNT].displayName,
    sentencePrefix: 'Count',
  },
  {
    key: 'count-distinct-units',
    name: 'Count distinct units (Percent)',
    description: 'Whether an event is seen at least one time ex: Errors',
    tag: MetricKindDisplays[MetricKind.BINARY].displayName,
    sentencePrefix: 'Percent',
  },
  {
    key: 'sum',
    name: 'Sum',
    description: 'Total value per unit ex: If a customer makes 3 purchases, add up their total',
    tag: MetricKindDisplays[MetricKind.NUMERIC].displayName,
    sentencePrefix: 'Sum',
  },
  {
    key: 'average',
    name: 'Average',
    description: 'Average value per unit ex: If a customer makes 3 purchases, divide their total by 3',
    tag: MetricKindDisplays[MetricKind.NUMERIC].displayName,
    sentencePrefix: 'Average',
  },
];

export type RandomizationUnitOption = {
  label: string;
  value: string;
  default?: boolean;
};

//Deprecate me after new metric edit modal is launched
export function fromMetric(metric: Metric): EditMetricForm {
  const { urls, randomizationUnits, tags, ...rest } = metric;
  return {
    ...rest,
    randomizationUnits: randomizationUnits?.map((value) => ({ label: value, value })) ?? [],
    tags: tags?.map((value) => ({ label: value, value })),
    urls: urls?.filter(isMetricEventUrl).map(fromMetricEventUrl),
    unitAggregationType: rest?.unitAggregationType ?? 'average',
    analysisType: rest?.analysisType ?? 'mean',
    eventDefault: {
      disabled: rest.eventDefault?.disabled ?? false,
      ...(rest.eventDefault?.value !== undefined && { value: rest.eventDefault.value }),
    },
  };
}

export function fromMetricToEditForm(metric: Metric): MetricForm {
  const { urls, randomizationUnits, tags, filters, ...rest } = metric;
  let measureType: MeasureType = 'value';
  if (!metric.isNumeric) {
    if (metric.unitAggregationType === 'average') {
      measureType = 'occurrence';
    } else {
      measureType = 'count';
    }
  }

  let typecastedFilters: MetricFilter | undefined;
  if (filters) {
    typecastedFilters = convertFilters(filters);
  }

  // Detect unit from the end offset when start is 0, since 0 is divisible by every unit.
  const windowUnitSource = metric.windowStartOffset || metric.windowEndOffset;
  const windowUnit = windowUnitSource !== undefined ? detectWindowUnit(windowUnitSource) : 'days';
  const toUnit =
    windowUnit === 'days' ? millisecondsToDays : windowUnit === 'hours' ? millisecondsToHours : millisecondsToMinutes;

  // Map API winsorization fields to form structure. Default missing bounds to
  // the no-op values the create flow uses so partial winsorization can be edited.
  const { winsorLowerPercentile, winsorUpperPercentile } = metric;
  const hasWinsorization = winsorLowerPercentile !== undefined || winsorUpperPercentile !== undefined;
  const winsorization:
    | {
        lowerPercentile?: number;
        upperPercentile?: number;
      }
    | undefined = hasWinsorization
    ? {
        lowerPercentile: winsorLowerPercentile ?? 0,
        upperPercentile: winsorUpperPercentile ?? 100,
      }
    : undefined;

  const apiDenominator = metric.denominator;
  let denominatorForm: DenominatorForm | undefined;
  if (apiDenominator) {
    const denomWindowUnitSource = apiDenominator.windowStartOffset || apiDenominator.windowEndOffset;
    const denomWindowUnit = denomWindowUnitSource !== undefined ? detectWindowUnit(denomWindowUnitSource) : 'days';
    const toDenomUnit =
      denomWindowUnit === 'days'
        ? millisecondsToDays
        : denomWindowUnit === 'hours'
          ? millisecondsToHours
          : millisecondsToMinutes;

    const denom = apiDenominator;
    const denomHasWinsorization =
      apiDenominator.winsorLowerPercentile !== undefined || apiDenominator.winsorUpperPercentile !== undefined;
    function getDenomAggregationType(): RatioTermAggregation {
      const uatStr = denom.unitAggregationType;
      if (uatStr === 'count_distinct') {
        return 'count_distinct';
      }
      if (denom.isNumeric && uatStr === 'sum') {
        return 'sum';
      }
      if (denom.isNumeric && uatStr === 'average') {
        return 'average';
      }
      return 'count';
    }

    denominatorForm = {
      eventKey: apiDenominator.eventName ?? '',
      dataSourceKey: apiDenominator.dataSource?.key,
      aggregationType: getDenomAggregationType(),
      unitAggregationField: apiDenominator.unitAggregationField,
      windowMode:
        apiDenominator.windowStartOffset !== undefined || apiDenominator.windowEndOffset !== undefined
          ? 'custom'
          : 'none',
      windowUnit: denomWindowUnit,
      windowStart:
        apiDenominator.windowStartOffset !== undefined ? toDenomUnit(apiDenominator.windowStartOffset) : undefined,
      windowEnd: apiDenominator.windowEndOffset !== undefined ? toDenomUnit(apiDenominator.windowEndOffset) : undefined,
      winsorLowerEnabled: denomHasWinsorization,
      winsorLowerPercentile: denomHasWinsorization ? (apiDenominator.winsorLowerPercentile ?? 0) : undefined,
      winsorUpperEnabled: denomHasWinsorization,
      winsorUpperPercentile: denomHasWinsorization ? (apiDenominator.winsorUpperPercentile ?? 100) : undefined,
    };
  }

  return {
    ...rest,
    filters: typecastedFilters,
    randomizationUnits: randomizationUnits?.map((value) => ({ label: value, value })) ?? [],
    tags: tags?.map((value) => ({ label: value, value })),
    urls: urls?.filter(isMetricEventUrl).map(fromMetricEventUrl),
    unitAggregationType: rest?.unitAggregationType ?? 'average',
    analysisType: rest?.analysisType ?? 'mean',
    //TODO: what should happen if an invalid maintainer id is loaded?
    maintainerId: metric.maintainerId ?? '',
    eventDefault: {
      disabled: rest.eventDefault?.disabled ?? false,
      ...(rest.eventDefault?.value !== undefined && { value: rest.eventDefault.value }),
    },
    measureType,
    ratioOrSimple: apiDenominator ? 'ratio' : 'simple',
    dataSourceKey: metric.dataSource?.key,
    windowMode: metric.windowStartOffset !== undefined || metric.windowEndOffset !== undefined ? 'custom' : 'none',
    windowUnit,
    windowStart: metric.windowStartOffset !== undefined ? toUnit(metric.windowStartOffset) : undefined,
    windowEnd: metric.windowEndOffset !== undefined ? toUnit(metric.windowEndOffset) : undefined,
    traceQuery: metric.traceQuery,
    winsorization,
    denominator: denominatorForm,
  };
}

// fields that are included in the create metric modal, deprecate after new create modal launch
export type NewMetricForm = EditMetricForm & {
  key: string;
  kind: 'click' | 'pageview' | 'custom';
  isNumeric?: boolean;
  maintainerId: string;
};

export type WindowUnit = 'days' | 'hours' | 'minutes';

/** Aggregation type for a ratio metric term (numerator or denominator). */
export type RatioTermAggregation = 'count' | 'sum' | 'average' | 'count_distinct';

/** Form state for the denominator term of a ratio metric. */
export type DenominatorForm = {
  eventKey: string;
  dataSourceKey?: string;
  aggregationType: RatioTermAggregation;
  unitAggregationField?: string;

  windowMode: 'none' | 'custom';
  windowUnit: WindowUnit;
  windowStart?: number;
  windowEnd?: number;
  winsorLowerEnabled?: boolean;
  winsorLowerPercentile?: number;
  winsorUpperEnabled?: boolean;
  winsorUpperPercentile?: number;
};

export const defaultDenominatorForm: DenominatorForm = {
  eventKey: '',
  aggregationType: 'count',
  windowMode: 'none',
  windowUnit: 'days',
  winsorLowerEnabled: false,
  winsorUpperEnabled: false,
};

function detectWindowUnit(ms: number): WindowUnit {
  if (ms % daysToMilliseconds(1) === 0) {
    return 'days';
  }
  if (ms % hoursToMilliseconds(1) === 0) {
    return 'hours';
  }
  return 'minutes';
}

export type MetricForm = {
  key: string;
  kind: Metric['kind'];
  isNumeric?: boolean;
  maintainerId: string;
  name: string;
  description?: string;
  eventKey?: string;
  unit?: string;
  filters?: MetricFilter;
  successCriteria?: Metric['successCriteria'];
  urls?: Array<{ kind: MetricEventUrlKind; value: string }>;
  selector?: string;
  tags?: Array<{ label: string; value: string }> | null;
  randomizationUnits: RandomizationUnitOption[];
  unitAggregationType?: Metric['unitAggregationType'];
  analysisType?: Metric['analysisType'];
  percentileValue?: number;
  eventDefault: { disabled: boolean; value?: number };
  measureType?: MeasureType;
  dataSourceKey?: MetricDataSource['key'];
  /** Controls whether the measurement window is active. */
  windowMode: 'none' | 'custom';
  /** Unit used for displaying/entering window offsets. */
  windowUnit: WindowUnit;
  /** Window start offset in the selected unit (converted to ms when sent to the API). */
  windowStart?: number;
  /** Window end offset in the selected unit (converted to ms when sent to the API). */
  windowEnd?: number;
  /** ANTLR query string for trace metrics, derived from the QueryBuilder. */
  traceQuery?: string;
  /** For numeric trace metrics, the span attribute key whose value is measured. */
  traceValueLocation?: string;
  /** Whether this is a simple or ratio metric. Only applies to custom metrics. */
  ratioOrSimple?: 'simple' | 'ratio';
  /** Warehouse column to count distinct values of. Required when unitAggregationType is count_distinct. */
  unitAggregationField?: string;
  /** Denominator configuration for ratio metrics. */
  denominator?: DenominatorForm;
  /** Winsorization configuration for handling outliers. */
  winsorization?: {
    lowerPercentile?: number;
    upperPercentile?: number;
    /** Hidden validation field to ensure at least one bound is configured */
    _validation?: unknown;
  };
};

// fields that are editable in the metric details form, deprecate after new edit modal launch
export type EditMetricForm = {
  name: string;
  description?: string;
  eventKey?: string;
  unit?: string;
  successCriteria?: Metric['successCriteria'];
  urls?: Array<{ kind: MetricEventUrlKind; value: string }>;
  selector?: string;
  tags?: Array<{ label: string; value: string }> | null;
  randomizationUnits: Array<{ label: string; value: string }>;
  unitAggregationType: 'average' | 'sum' | 'count_distinct';
  analysisType: 'mean' | 'percentile';
  percentileValue?: number;
  eventDefault: { disabled: boolean; value?: number };
  maintainerId?: string;
};

export const FIELD_NAME_TO_ACTION = new Map<string, ResourceActions<'metric'>>([
  ['name', 'updateName'],
  ['description', 'updateDescription'],
  ['urls', 'updateUrls'],
  ['selector', 'updateSelector'],
  ['tags', 'updateTags'],
  ['isNumeric', 'updateNumeric'],
  ['unit', 'updateNumericUnit'],
  ['successCriteria', 'updateNumericSuccess'],
  ['maintainerId', 'updateMaintainer'],
  ['eventKey', 'updateEventKey'],
  ['randomizationUnits', 'updateRandomizationUnits'],
  ['unitAggregationType', 'updateUnitAggregationType'],
  ['analysisType', 'updateAnalysisType'],
  ['eventDefault', 'updateEventDefault'],
]);

export type DenominatorInput = {
  eventName: string;
  isNumeric: boolean;
  unitAggregationType?: string;
  unitAggregationField?: string;
  dataSource?: { key: string };
  windowStartOffset?: number;
  windowEndOffset?: number;
  winsorLowerPercentile?: number;
  winsorUpperPercentile?: number;
};

export const defaultContextAttributeFilter: ContextAttributeFilter = {
  type: 'contextAttribute',
  op: 'in',
  contextKind: 'user',
  values: [],
  negate: false,
};

export const defaultEventPropertyFilter: EventPropertyFilter = {
  type: 'eventProperty',
  op: 'in',
  values: [],
  negate: false,
};
